import {
    MapContainer,
    TileLayer,
    Marker,
    Tooltip,
    Popup,
} from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import {
    HStack,
    VStack,
    Text,
    Heading,
    Divider,
    useDisclosure,
    Drawer,
    DrawerBody,
    DrawerCloseButton,
    DrawerContent,
    DrawerHeader,
    DrawerOverlay,
} from '@chakra-ui/react';
import { useCallback, useState } from 'react';
import L from 'leaflet';
import "leaflet/dist/leaflet.css";
import useIsMobile from '../hooks/useIsMobile.js';
import { iconSelector, getWorstStatus } from '../helpers/iconSelector.js';
import { STATUS_LABEL } from '../constants/status.js';
import DataComposer from './DataComposer.js';
import { InfoIcon } from '@chakra-ui/icons';

const corner1 = L.latLng(-11.011774, -102.208789);
const corner2 = L.latLng(-57.234297, -25.698563);
const bounds = L.latLngBounds(corner1, corner2);

const Map = ({ stationsData }) => {

    const { isOpen, onOpen, onClose } = useDisclosure();
    const [selectedStation, setSelectedStation] = useState(null);
    const isMobile = useIsMobile();

    const handleMarkerClick = useCallback((station) => {
        setSelectedStation(station);
        onOpen();
    }, [onOpen]);

    return (
        <>
            <MapContainer
                maxBounds={bounds}
                zoomControl={true}
                minZoom={4}
                dragging={true}
                center={[-34.135020, -71.565964]}
                zoom={4}
                scrollWheelZoom={true}>

                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />

                <MarkerClusterGroup>
                    {stationsData.map((station) => {
                        const worstStatus = getWorstStatus(station.realtime);
                        return <Marker
                            icon={iconSelector(station.realtime, worstStatus)}
                            key={station.key}
                            position={[station.latitud, station.longitud]}
                            title={`${station.nombre} — ${STATUS_LABEL[worstStatus]}`}
                            eventHandlers={isMobile ? {
                                click: () => handleMarkerClick(station)
                            } : {}}
                        >
                            {!isMobile && (
                                <Popup minWidth={600} autoPan={false}>
                                    <VStack spacing={3} align='stretch' w={600}>
                                        <Heading size='md' color='cadetblue'>{station.nombre}</Heading>
                                        <DataComposer data={station.realtime} />
                                    </VStack>
                                </Popup>
                            )}
                            {!isMobile && (
                                <Tooltip opacity={1} direction='bottom'>
                                    <VStack spacing={3} align='stretch'>
                                        <Heading size='md' color='cadetblue'>{station.nombre}</Heading>
                                        <Text fontSize='sm' color='gray.600'>{station.region}</Text>
                                        <Text fontSize='sm' color='gray.600'>Comuna : {station.comuna}</Text>
                                        <Divider />
                                        <Text fontSize='sm' color='gray.600'>Latitud : {station.latitud}</Text>
                                        <Text fontSize='sm' color='gray.600'>Longitud : {station.longitud}</Text>
                                        <HStack>
                                            <InfoIcon mb='3px' color='gray.600' boxSize='1.5em' />
                                            <Text fontSize='md' color='cadetblue'>Click para más información</Text>
                                        </HStack>
                                    </VStack>
                                </Tooltip>
                            )}
                        </Marker>;
                    })}
                </MarkerClusterGroup>
            </MapContainer>

            {isMobile && (
                <Drawer placement='bottom' isOpen={isOpen} onClose={onClose}>
                    <DrawerOverlay />
                    <DrawerContent borderTopRadius='16px' maxH='85vh' bg='gray.400'>
                        <DrawerCloseButton />
                        <DrawerHeader pb={1}>
                            {selectedStation && (
                                <VStack align='start' spacing={0}>
                                    <Heading size='sm' color='cadetblue'>
                                        {selectedStation.nombre}
                                    </Heading>
                                    <Text fontSize='xs' color='gray.700'>
                                        {selectedStation.region}
                                    </Text>
                                    <Text fontSize='xs' color='gray.700'>
                                        Comuna: {selectedStation.comuna}
                                    </Text>
                                </VStack>
                            )}
                        </DrawerHeader>
                        <DrawerBody overflowY='auto' pb={6}>
                            {selectedStation && (
                                <DataComposer data={selectedStation.realtime} />
                            )}
                        </DrawerBody>
                    </DrawerContent>
                </Drawer>
            )}
        </>
    );
};

export default Map;
